import { useState } from "react";
import { useApolloClient } from "@apollo/client";
import { useParams } from "react-router-dom";
import { IParams } from "@models/IParams";
import { GroupEnrollment, PageInfo } from "@models/ISchema";
import { groupEnrollmentsByLinkId } from "../graphql/groupEnrollmentsByLinkId.query";
import { AdaptedGroup } from "../models";
import { EnrollmentsTableFiltersState } from "../context/useEnrollmentsTableFilters";

interface IPagination {
  page: number;
  size: number;
}

const useGetGroupEnrollmentsByLinkId = (): [
  GroupEnrollment[],
  boolean,
  PageInfo,
  (
    group: AdaptedGroup,
    pagination: IPagination,
    orderBy: EnrollmentsTableFiltersState["orderBy"],
    searchBy: EnrollmentsTableFiltersState["searchBy"],
  ) => void,
] => {
  const { scenario, origin }: IParams = useParams();
  const client = useApolloClient();
  const [response, setResponse] = useState<GroupEnrollment[]>(null);
  const [pageInfo, setPageInfo] = useState<PageInfo>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const variables = (
    group: AdaptedGroup,
    pagination: IPagination,
    orderBy: EnrollmentsTableFiltersState["orderBy"],
    searchBy: EnrollmentsTableFiltersState["searchBy"],
  ) => {
    return {
      scenarioId: scenario,
      originId: origin,
      linkId: group?.link?.id,
      filter: {
        pagination: {
          page: pagination?.page ?? 1,
          size: pagination?.size ?? 1,
        },
        orderBy:
          !orderBy || Object.keys(orderBy).length === 0 || orderBy?.field == null
            ? undefined
            : (() => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { header, ...rest } = orderBy;
                return rest;
              })(),
        searchBy:
          !searchBy || Object.keys(searchBy).length === 0 || searchBy?.text == null
            ? undefined
            : searchBy,
      },
    };
  };

  const getGroupEnrollments = async (
    group: AdaptedGroup,
    pagination: IPagination,
    orderBy: EnrollmentsTableFiltersState["orderBy"],
    searchBy: EnrollmentsTableFiltersState["searchBy"],
  ) => {
    setLoading(true);
    try {
      const { data, errors } = await client.query({
        query: groupEnrollmentsByLinkId,
        variables: variables(group, pagination, orderBy, searchBy),
        fetchPolicy: "no-cache",
      });

      if (errors) {
        console.error("[GroupEnrollments error]", errors);
        setLoading(false);
        return null;
      }

      setResponse(data?.cube?.groupEnrollments?.items);
      setPageInfo(data?.cube?.groupEnrollments?.pageInfo);
      setLoading(false);
    } catch (error) {
      console.error("[GroupEnrollments error]: ", error);
      setLoading(false);
    }
  };

  return [response, loading, pageInfo, getGroupEnrollments];
};

export default useGetGroupEnrollmentsByLinkId;
